// IndexedDB 数据库配置
const DB_NAME = 'aiproduct_cache';
const DB_VERSION = 3; // 增加版本号以触发数据库升级
const STORE_NAME = 'products';
const CACHE_EXPIRY = 7 * 24 * 60 * 60 * 1000; // 7天过期

// 判断是否是最新日期范围
const isLatestDateRange = (dateRange, allDateRanges) => {
    if (!dateRange || !allDateRanges || allDateRanges.length === 0) return false;
    
    // 按日期排序所有范围
    const sortedRanges = [...allDateRanges].sort((a, b) => {
        const aStart = a.split(' - ')[0];
        const bStart = b.split(' - ')[0];
        return new Date(bStart) - new Date(aStart);
    });
    
    return sortedRanges[0] === dateRange;
};

// 初始化数据库
const initDB = () => {
    return new Promise((resolve) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = (event) => {
            console.error('[IndexedDB] 数据库打开失败:', event.target.error);
            resolve(null);
        };

        request.onsuccess = (event) => {
            // console.log('[IndexedDB] 数据库连接成功');
            resolve(event.target.result);
        };

        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            
            // 如果存在旧的存储对象，先删除
            if (db.objectStoreNames.contains(STORE_NAME)) {
                db.deleteObjectStore(STORE_NAME);
            }
            
            // 创建新的存储对象
            const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
            
            // 创建索引
            store.createIndex('timestamp', 'timestamp', { unique: false });
            store.createIndex('dateRange', 'dateRange', { unique: false });
            
            // console.log('[IndexedDB] 数据存储对象创建成功');
        };
    });
};

// 存储单个日期范围的数据
const storeData = async (data, dateRange) => {
    try {
        const db = await initDB();
        if (!db) return false;

        return new Promise((resolve) => {
            const transaction = db.transaction(STORE_NAME, 'readwrite');
            const store = transaction.objectStore(STORE_NAME);

            const dataToStore = {
                id: dateRange || 'main-cache',
                data: Array.isArray(data) ? data : [],
                dateRange: dateRange,
                timestamp: Date.now()
            };

            const request = store.put(dataToStore);

            request.onsuccess = () => {
                // console.log(`[IndexedDB] 数据存储成功: ${dateRange}`);
                resolve(true);
            };

            request.onerror = (e) => {
                console.error(`[IndexedDB] data cache error: ${dateRange}`, e.target.error);
                resolve(false);
            };

            transaction.oncomplete = () => db.close();
        });
    } catch (error) {
        console.error('[IndexedDB] data cache error:', error);
        return false;
    }
};

// 批量存储多个日期范围的数据
const storeBatchData = async (dataMap) => {
    try {
        const db = await initDB();
        if (!db) return false;

        return new Promise((resolve) => {
            const transaction = db.transaction(STORE_NAME, 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            let completed = 0;
            let failed = 0;

            Object.entries(dataMap).forEach(([dateRange, data]) => {
                const request = store.put({
                    id: dateRange,
                    data: Array.isArray(data) ? data : [],
                    dateRange: dateRange,
                    timestamp: Date.now()
                });

                request.onsuccess = () => {
                    completed++;
                    if (completed + failed === Object.keys(dataMap).length) {
                        resolve(failed === 0);
                    }
                };

                request.onerror = () => {
                    failed++;
                    if (completed + failed === Object.keys(dataMap).length) {
                        resolve(false);
                    }
                };
            });

            transaction.oncomplete = () => db.close();
        });
    } catch (error) {
        console.error('[IndexedDB] batch data cache:', error);
        return false;
    }
};

// 获取单个日期范围的数据
const getData = async (dateRange, allDateRanges = []) => {
    try {
        // 如果是最新日期范围，直接返回null以触发从服务器加载
        if (isLatestDateRange(dateRange, allDateRanges)) {
            // console.log('[IndexedDB] Latest date range detected, skipping cache');
            return null;
        }

        const db = await initDB();
        if (!db) return null;

        return new Promise((resolve) => {
            const transaction = db.transaction(STORE_NAME, 'readonly');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.get(dateRange || 'main-cache');

            request.onsuccess = () => {
                const result = request.result;
                if (!result || Date.now() - result.timestamp > CACHE_EXPIRY) {
                    resolve(null); // 数据不存在或已过期
                } else {
                    resolve(result.data);
                }
            };

            request.onerror = () => resolve(null);

            transaction.oncomplete = () => db.close();
        });
    } catch (error) {
        console.error('[IndexedDB] data read:', error);
        return null;
    }
};

// 获取所有缓存的数据
const getAllData = async (allDateRanges = []) => {
    try {
        const db = await initDB();
        if (!db) return {};

        return new Promise((resolve) => {
            const transaction = db.transaction(STORE_NAME, 'readonly');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.getAll();

            request.onsuccess = () => {
                const results = request.result;
                const validData = {};
                
                results.forEach(item => {
                    // 对于最新日期范围的数据，不从缓存中获取
                    if (isLatestDateRange(item.dateRange, allDateRanges)) {
                        return;
                    }
                    
                    // 检查数据是否过期
                    if (Date.now() - item.timestamp <= CACHE_EXPIRY) {
                        validData[item.dateRange] = item.data;
                    }
                });
                
                resolve(validData);
            };

            request.onerror = () => resolve({});

            transaction.oncomplete = () => db.close();
        });
    } catch (error) {
        console.error('[IndexedDB] all data read error:', error);
        return {};
    }
};

// 清理过期数据
const cleanExpiredData = async () => {
    try {
        const db = await initDB();
        if (!db) return;

        const transaction = db.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const index = store.index('timestamp');
        const expiryTime = Date.now() - CACHE_EXPIRY;
        
        return new Promise((resolve) => {
            const request = index.openCursor(IDBKeyRange.upperBound(expiryTime));
            
            request.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    store.delete(cursor.primaryKey);
                    cursor.continue();
                } else {
                    resolve();
                }
            };
            
            request.onerror = (error) => {
                console.error('[IndexedDB] data clean error:', error);
                resolve();
            };
            
            transaction.oncomplete = () => {
                db.close();
                resolve();
            };
        });
    } catch (error) {
        console.error('[IndexedDB] data clean error:', error);
    }
};

export { 
    initDB, 
    storeData, 
    storeBatchData, 
    getData, 
    getAllData, 
    cleanExpiredData,
    isLatestDateRange  // 确保导出这个函数
};